import { useEffect, useState } from "react";
import { Plus, ShieldCheck, Lock, Trash2, Check, Pencil, GitCompare, Minus, Layers, Users, Grid3x3, Search, ArrowRightLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { roleEffective, audiencesForRole } from "@/lib/access";
import AudienceMatrix from "@/components/permissions/AudienceMatrix";
import RoleMatrix from "@/components/permissions/RoleMatrix";
import RoleDialog from "@/components/permissions/RoleDialog";
import ExplainAccessTab from "@/components/permissions/ExplainAccess";
import AccessSyncTab from "@/components/permissions/AccessSyncTab";
import RolePresets from "@/components/permissions/RolePresets";

export default function Permissions() {
  const { has, user } = useAuth();
  const canManage = has("permissions.edit");
  const isSuper = user?.role === "super_admin";
  const [roles, setRoles] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteMembers, setDeleteMembers] = useState({ count: 0, users: [] });
  const [reassignTo, setReassignTo] = useState("");

  const loadRoles = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  const loadAudiences = () => api.get("/app-audiences").then((r) => setAudiences(r.data.audiences || [])).catch(() => {});
  const load = () => {
    loadRoles();
    loadAudiences();
    api.get("/permissions/matrix").then((r) => setCatalog(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setDialogOpen(true); };

  const remove = async () => {
    try {
      const q = deleteMembers.count > 0 ? `?reassign_to=${encodeURIComponent(reassignTo)}` : "";
      const { data } = await api.delete(`/roles/${deleteTarget.role_id}${q}`);
      toast.success(data?.moved ? `Role deleted · ${data.moved} user${data.moved === 1 ? "" : "s"} moved` : "Role deleted");
      setDeleteTarget(null);
      loadRoles();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const askDelete = (r) => {
    setDeleteTarget(r);
    setReassignTo("");
    setDeleteMembers({ count: r.user_count ?? 0, users: [] });
    api.get(`/roles/${r.role_id}/users`).then((res) => setDeleteMembers(res.data)).catch(() => {});
  };

  return (
    <div>
      <PageHeader testid="permissions-header" title="Permissions" subtitle="Audience defaults → role adjustments → per-person tweaks. Super Admin always has everything.">
        {canManage && (
          <Button data-testid="add-role-button" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Plus className="h-4 w-4" /> New custom role
          </Button>
        )}
      </PageHeader>

      <Tabs defaultValue="audiences">
        <TabsList className="mb-4 flex h-auto flex-wrap">
          <TabsTrigger value="audiences" data-testid="perm-tab-audiences"><Users className="mr-1.5 h-3.5 w-3.5" /> Audiences</TabsTrigger>
          <TabsTrigger value="roles" data-testid="perm-tab-roles"><Layers className="mr-1.5 h-3.5 w-3.5" /> Roles</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="perm-tab-matrix"><Grid3x3 className="mr-1.5 h-3.5 w-3.5" /> Role Matrix</TabsTrigger>
          <TabsTrigger value="compare" data-testid="perm-tab-compare"><GitCompare className="mr-1.5 h-3.5 w-3.5" /> Compare Roles</TabsTrigger>
          <TabsTrigger value="explain" data-testid="perm-tab-explain"><Search className="mr-1.5 h-3.5 w-3.5" /> Explain Access</TabsTrigger>
          {isSuper && <TabsTrigger value="sync" data-testid="perm-tab-sync"><ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" /> Sync</TabsTrigger>}
        </TabsList>

        <TabsContent value="audiences">
          <AudienceMatrix catalog={catalog} audiences={audiences} canManage={canManage} onChanged={loadAudiences} />
        </TabsContent>

        <TabsContent value="roles">
          <RolePresets roles={roles} audiences={audiences} canManage={canManage}
            onApplied={(data) => setRoles((rs) => rs.map((x) => (x.role_id === data.role_id ? { ...x, ...data } : x)))} />
          <RolesTable roles={roles} audiences={audiences} canManage={canManage} onEdit={openEdit} onDelete={askDelete} />
        </TabsContent>

        <TabsContent value="matrix">
          <RoleMatrix catalog={catalog} roles={roles} audiences={audiences} canManage={canManage}
            onRolesChanged={loadRoles}
            onRoleUpdated={(data) => setRoles((rs) => rs.map((x) => (x.role_id === data.role_id ? { ...x, ...data } : x)))} />
        </TabsContent>

        <TabsContent value="compare">
          <CompareRoles roles={roles} audiences={audiences} catalog={catalog} />
        </TabsContent>

        <TabsContent value="explain">
          <ExplainAccessTab catalog={catalog} />
        </TabsContent>

        {isSuper && (
          <TabsContent value="sync">
            <AccessSyncTab />
          </TabsContent>
        )}
      </Tabs>

      <RoleDialog open={dialogOpen} onClose={() => setDialogOpen(false)} role={editing}
        catalog={catalog} audiences={audiences} onSaved={loadRoles} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteMembers.count > 0
                ? `${deleteMembers.count} ${deleteMembers.count === 1 ? "person is" : "people are"} still on “${deleteTarget?.name}”. Move them to another role first — otherwise they lose their access and disappear from staff boards.`
                : `This removes the custom role “${deleteTarget?.name}”. Nobody is assigned to it.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteMembers.count > 0 && (
            <div className="space-y-3" data-testid="role-delete-members">
              <ul className="max-h-32 space-y-1 overflow-auto rounded-lg border border-amber-200 bg-amber-50 p-2">
                {deleteMembers.users.map((u) => (
                  <li key={u.user_id} className="text-xs text-amber-900" data-testid={`role-delete-member-${u.user_id}`}>
                    <span className="font-semibold">{u.name || u.email}</span> <span className="text-amber-700/80">{u.email}</span>
                  </li>
                ))}
              </ul>
              <div className="space-y-1.5">
                <Label>Move these users to</Label>
                <Select value={reassignTo} onValueChange={setReassignTo}>
                  <SelectTrigger data-testid="role-delete-reassign"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                  <SelectContent>
                    {roles.filter((r) => r.role_id !== deleteTarget?.role_id).map((r) => (
                      <SelectItem key={r.role_id} value={r.role_id} data-testid={`role-delete-reassign-${r.role_id}`}>
                        {r.name} <span className="text-muted-foreground">· {r.base_role}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-role" onClick={remove}
              disabled={deleteMembers.count > 0 && !reassignTo}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50">
              {deleteMembers.count > 0 ? "Move users & delete" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RolesTable({ roles, audiences, canManage, onEdit, onDelete }) {
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="roles-list">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Role</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Base</TableHead>
            <TableHead>Inherits from</TableHead>
            <TableHead className="text-right">Effective</TableHead>
            <TableHead className="text-right">Granted</TableHead>
            <TableHead className="text-right">Revoked</TableHead>
            <TableHead className="text-right">Users</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roles.map((r) => {
            const auds = audiencesForRole(r, audiences);
            return (
              <TableRow key={r.role_id} data-testid={`role-row-${r.role_id}`}>
                <TableCell className="py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#eef2fb] text-primary"><ShieldCheck className="h-4 w-4" /></span>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{r.name}</div>
                      <div className="max-w-md truncate text-xs text-slate-400">{r.description}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${r.is_system ? "bg-slate-100 text-slate-500" : "bg-[#eef2fb] text-primary"}`}>
                    {r.is_system ? "System" : "Custom"}
                  </span>
                </TableCell>
                <TableCell className="text-sm capitalize text-slate-600">{r.base_role}</TableCell>
                <TableCell className="text-xs text-slate-500" data-testid={`role-audiences-${r.role_id}`}>
                  {r.role_id === "super_admin" ? "Everything" : auds.length ? auds.map((a) => a.label).join(", ") : <span className="text-slate-300">no audience</span>}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-slate-700" data-testid={`role-effective-${r.role_id}`}>{roleEffective(r, audiences).size}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-primary">{r.role_id === "super_admin" ? "—" : `+${r.permissions?.length || 0}`}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-rose-600">{r.role_id === "super_admin" ? "—" : `−${r.revoked?.length || 0}`}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-slate-600">{r.user_count ?? 0}</TableCell>
                <TableCell className="text-right">
                  {r.role_id === "super_admin" ? (
                    <Lock className="ml-auto h-4 w-4 text-slate-300" />
                  ) : canManage ? (
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-role-${r.role_id}`} onClick={() => onEdit(r)}><Pencil className="h-4 w-4" /></Button>
                      {!r.is_system && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" data-testid={`delete-role-${r.role_id}`} onClick={() => onDelete(r)}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function CompareRoles({ roles, audiences, catalog }) {
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [diffOnly, setDiffOnly] = useState(true);
  useEffect(() => {
    if (roles.length && !compareA) setCompareA(roles[0].role_id);
    if (roles.length > 1 && !compareB) setCompareB(roles[1].role_id);
  }, [roles]); // eslint-disable-line

  const byId = (id) => roles.find((r) => r.role_id === id);
  const roleName = (id) => byId(id)?.name || "—";
  const aSet = roleEffective(byId(compareA), audiences), bSet = roleEffective(byId(compareB), audiences);
  const rows = []; let diffCount = 0;
  (catalog?.modules || []).forEach((m) => m.actions.forEach((act) => {
    const perm = `${m.id}.${act}`;
    const a = aSet.has(perm), b = bSet.has(perm);
    if (a !== b) diffCount += 1;
    if (diffOnly && a === b) return;
    rows.push({ perm, label: m.label, act, a, b });
  }));

  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid="compare-card">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label>Role A</Label>
          <Select value={compareA} onValueChange={setCompareA}>
            <SelectTrigger className="w-52" data-testid="compare-role-a"><SelectValue placeholder="Select role" /></SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <GitCompare className="mb-2 h-5 w-5 text-slate-300" />
        <div className="space-y-1.5">
          <Label>Role B</Label>
          <Select value={compareB} onValueChange={setCompareB}>
            <SelectTrigger className="w-52" data-testid="compare-role-b"><SelectValue placeholder="Select role" /></SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="mb-2 ml-auto flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={diffOnly} onCheckedChange={() => setDiffOnly((v) => !v)} data-testid="compare-diff-only" /> Show differences only
        </label>
      </div>
      <div className="mt-3 text-sm" data-testid="compare-summary">
        <span className="font-semibold text-amber-600">{diffCount}</span> difference{diffCount === 1 ? "" : "s"} between <span className="font-medium">{roleName(compareA)}</span> and <span className="font-medium">{roleName(compareB)}</span>
        <span className="ml-2 text-xs text-slate-400">(effective permissions — audience defaults included; partner roles use every audience for their base role)</span>
      </div>
      <div className="mt-3 overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm" data-testid="compare-table">
          <thead><tr className="border-b border-slate-100 text-left">
            <th className="px-4 py-2 font-medium text-slate-500">Module / Action</th>
            <th className="px-4 py-2 text-center font-medium text-slate-500 whitespace-nowrap">{roleName(compareA)}</th>
            <th className="px-4 py-2 text-center font-medium text-slate-500 whitespace-nowrap">{roleName(compareB)}</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={3} className="py-8 text-center text-slate-400">{diffOnly ? "No differences — these roles have identical permissions." : "No permissions to display."}</td></tr>
            ) : rows.map((row) => (
              <tr key={row.perm} className={`border-b border-slate-50 ${row.a !== row.b ? "bg-amber-50" : ""}`} data-testid={`compare-row-${row.perm}`}>
                <td className="px-4 py-2 text-slate-700 whitespace-nowrap"><span className="font-medium">{row.label}</span> <span className="text-slate-400">· {row.act}</span></td>
                <td className="px-4 py-2 text-center">{row.a ? <Check className="mx-auto h-4 w-4 text-green-600" /> : <Minus className="mx-auto h-4 w-4 text-slate-300" />}</td>
                <td className="px-4 py-2 text-center">{row.b ? <Check className="mx-auto h-4 w-4 text-green-600" /> : <Minus className="mx-auto h-4 w-4 text-slate-300" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
